import {
  ArrowUpwardIcon,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@rbx/ui';
import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { topNavigationHeight } from '@modules/layout/layout/constants/layoutConstants';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { useLocalization, useTranslation } from '@rbx/intl';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { TestId } from '@modules/utilities/constants/testConstants';
import { useProvidedConversationReducer } from '@rbx/conv-ai-provider';
import { assistantIsMobileBreakpoint } from '../constants/assistantStyleConstants';
import useAssistantInputStyles from './AssistantInput.styles';
import { useAssistantOptIn } from '../context/AssistantOptInProvider';

export type AssistantInputProps = {
  onSendPrompt: (prompt: string) => void;
  onStopResponse: (textareaInput: string) => () => void;
  streaming: boolean;
  onClientHeightChange: (height: number, containerHeight: number) => void;
};

const AssistantInput: FC<React.PropsWithChildren<AssistantInputProps>> = ({
  onSendPrompt,
  streaming,
  onStopResponse,
  onClientHeightChange,
}) => {
  const { translate } = useTranslation();
  const { locale } = useLocalization();
  const initializedTextInput = useRef<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  // TODO (@ahua, CRK-2919): UI needs translation strings
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down(assistantIsMobileBreakpoint));
  const {
    classes: {
      container,
      textField,
      helperTextWrapper,
      helperText,
      desktopSendButton,
      mobileSendButton,
      stopIcon,
      inputContainer,
      inputButtonWrapper,
    },
  } = useAssistantInputStyles();
  const {
    state: { conversationId },
  } = useProvidedConversationReducer();

  const { processing: processingOptIn } = useAssistantOptIn();
  const [textareaInput, setTextareaInput] = useState('');
  const onResize: (ev: Event) => void = useCallback(() => {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    if (ref.current)
      onClientHeightChange(ref.current.clientHeight, viewportHeight - topNavigationHeight);
  }, [ref, onClientHeightChange]);

  const ChatStopIcon = useCallback(() => <div className={stopIcon} />, [stopIcon]);

  const submitPrompt = useCallback(() => {
    onSendPrompt(textareaInput);
    setTextareaInput('');
  }, [onSendPrompt, textareaInput]);

  useEffect(() => {
    if (ref?.current) {
      onClientHeightChange(ref.current.clientHeight, window.innerHeight - topNavigationHeight);
      window.addEventListener('resize', onResize);
      window.visualViewport?.addEventListener('scroll', onResize);
      window.visualViewport?.addEventListener('resize', onResize);
      document.addEventListener('focusout', onResize);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      document.removeEventListener('focusout', onResize);
    };
  }, [ref, textareaInput, onClientHeightChange, onResize]);

  const inputAdornment = useMemo(() => {
    const disabled = processingOptIn || (!streaming && textareaInput.length === 0);
    const buttonColor = streaming ? 'inherit' : 'primary';
    const stopLabel = translate('Button.Stop');
    const sendLabel = translate('Button.Send');
    return (
      <React.Fragment>
        {isCompact ? (
          <Button
            className={mobileSendButton}
            disabled={disabled}
            aria-label={streaming ? stopLabel : sendLabel}
            size='medium'
            variant={streaming ? 'text' : 'contained'}
            color={buttonColor}
            onClick={streaming ? onStopResponse(textareaInput) : submitPrompt}>
            {streaming ? (
              <ChatStopIcon />
            ) : (
              <ArrowUpwardIcon
                color={textareaInput.length === 0 ? 'disabled' : 'inherit'}
                fontSize='medium'
              />
            )}
          </Button>
        ) : (
          <Button
            className={desktopSendButton}
            disabled={disabled}
            color={buttonColor}
            size='medium'
            variant={streaming ? 'text' : 'contained'}
            aria-label={streaming ? stopLabel : sendLabel}
            onClick={streaming ? onStopResponse(textareaInput) : submitPrompt}>
            {streaming && <ChatStopIcon />}
            {streaming ? stopLabel : sendLabel}
          </Button>
        )}
      </React.Fragment>
    );
  }, [
    processingOptIn,
    streaming,
    textareaInput,
    translate,
    isCompact,
    mobileSendButton,
    onStopResponse,
    submitPrompt,
    ChatStopIcon,
    desktopSendButton,
  ]);

  const onChangeTextareaInput = (e: ChangeEvent<HTMLInputElement>) => {
    setTextareaInput(e.currentTarget.value);
  };

  useEffect(() => {
    if (textareaInput.length && !initializedTextInput.current) {
      initializedTextInput.current = true;
      unifiedLoggerClient.logClickEvent({
        eventName: DocsAssistantEvent.UserStartedTypingTextInput,
        parameters: {
          locale: locale ?? '',
          conversationId: conversationId ?? '',
        },
      });
    }
    if (textareaInput.length === 0) {
      initializedTextInput.current = false;
      unifiedLoggerClient.logClickEvent({
        eventName: DocsAssistantEvent.UserClearedTextInput,
        parameters: {
          locale: locale ?? '',
          conversationId: conversationId ?? '',
        },
      });
    }
  }, [locale, textareaInput.length, conversationId]);

  return (
    <div ref={ref} className={container}>
      <TextField
        disabled={processingOptIn}
        className={textField}
        autoFocus
        aria-label={translate('Label.PromptTextInput')}
        data-testid={TestId.Input}
        id='input'
        variant='outlined'
        fullWidth
        multiline
        maxRows={8}
        value={textareaInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !streaming) {
            e.preventDefault();
            submitPrompt();
          }
        }}
        onKeyUp={() => {
          if (ref.current) {
            onClientHeightChange(
              ref.current.clientHeight,
              window.innerHeight - topNavigationHeight,
            );
          }
        }}
        label={translate('TextInput.Label')}
        onChange={onChangeTextareaInput}
        helperText={
          <Typography className={helperText} variant='footer' color='secondary' component='span'>
            {translate('TextInput.HelperText')}
          </Typography>
        }
        InputProps={{
          className: inputContainer,
          endAdornment: (
            <InputAdornment className={inputButtonWrapper} position='end'>
              {inputAdornment}
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          className: helperTextWrapper,
        }}
      />
    </div>
  );
};

export default AssistantInput;
